import type {
  ConciseBranch,
  ConciseBranches,
} from '@festina/configuration-general-types/configuration'
import { ConfigurationRepositoryBranchesService } from '@festina/configuration-general-types/configuration/services/ConfigurationRepositoryBranchesService'
import { type ConfigurableServer, notEmpty } from '@festina-life/core'
import { useSuspenseQuery } from '@tanstack/react-query'

import { fetchAllConciseBranchesKey } from '../keys/configuration'

export type AllConciseBranches = Partial<
  Record<ConfigurableServer, ConciseBranch[]>
>

export const fetchAllConciseBranch = async (
  servers: readonly ConfigurableServer[],
): Promise<AllConciseBranches> => {
  const fetchAll = servers.map((server) =>
    ConfigurationRepositoryBranchesService.branchServiceGetGetConciseBranches({
      componentName: server.toLocaleLowerCase(),
    }),
  )

  const result = await Promise.allSettled(fetchAll)

  return Object.fromEntries(
    servers.map(
      (server, serverIndex) =>
        [
          server,
          result[serverIndex].status === 'fulfilled'
            ? (
                (result[serverIndex] as PromiseFulfilledResult<ConciseBranches>)
                  .value?.branches ?? []
              ).filter(notEmpty)
            : [],
        ] as const,
    ),
  ) as AllConciseBranches
}

/**
 * Fetches the concise branches that exist for a user and a list of servers.
 */
export const useFetchAllConciseBranches = ({
  servers,
  enabled,
}: {
  servers: readonly ConfigurableServer[]
  enabled: boolean
}) =>
  useSuspenseQuery({
    queryKey: enabled ? fetchAllConciseBranchesKey(servers) : [],
    queryFn: async () => {
      if (!enabled) {
        return Promise.resolve(null)
      }

      return await fetchAllConciseBranch(servers)
    },
    staleTime: Infinity,
  })
